/**
 * The ROUTED half of the dirty-state guard (ROOT ruling 74071 §10).
 *
 * §10: *"Dirty-state protection requires both `beforeunload` and routed
 * navigation blocking, covering every exit #2328 lists."* One without the other
 * is the defect, because they cover disjoint exits:
 *
 *   · an in-app navigation never unloads the document, so `beforeunload` does
 *     not fire for it at all — this blocker is the only thing that sees it;
 *   · a reload or a tab close never reaches the router — `unsavedChanges.ts`'s
 *     `beforeunload` registration is the only thing that sees those.
 *
 * ── why the shared store owns the unload half ─────────────────────────────
 * `enableBeforeUnload: false` is deliberate, not an omission. TanStack's blocker
 * can install its own `beforeunload`, but `apps/portal` has **no router at all**
 * (`ui-and-experience.md` §4) and must still be guarded, so the unload half
 * lives in `@solum/ui` where both hosts reach it. Two registrations of one
 * listener would be the fork, and the one nobody looks at would be the one that
 * goes stale.
 *
 * ── it asks, and the browser dialog is the ANSWER, not the question ───────
 * `shouldBlockFn` returns *"block this navigation"*. With nothing unsaved it
 * returns false and no navigation is ever interrupted; with something unsaved it
 * asks, and a confirmed discard proceeds. The prompt is the shared registry's
 * (`confirmDiscardUnsaved`), so the sentence a person reads is the same one the
 * persona switch and sign out show.
 *
 * ── it draws nothing ─────────────────────────────────────────────────────
 * A component rather than a hook in `AppShell` because `AppShell` has early
 * returns above the point where this would sit; a component owns its own hook
 * order unconditionally. That is the same argument `ReleaseContextOnPageChange`
 * carries in the portal.
 */
import { useBlocker } from '@tanstack/react-router'
import { confirmDiscardUnsaved, hasUnsavedChanges } from '@solum/ui'

export function UnsavedChangesGuard() {
  useBlocker({
    shouldBlockFn: () => (hasUnsavedChanges() ? !confirmDiscardUnsaved() : false),
    enableBeforeUnload: false,
  })
  return null
}
